import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE } from '../config';
import { saveTenancy, productLanding, readServerMessage } from '../tenancy';
import { saveLabLink, isPathologyOnly } from '../labLink';
// HMSERP ka naam/logo sirf hmserp ke build me — Kamla par purana (src/brand.js)
import { IS_HMSERP } from '../brand';

const Login = ({ onLoginSuccess }) => {
  const [loginId, setLoginId] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [focusedInput, setFocusedInput] = useState(null);
  const [greeting, setGreeting] = useState("Welcome Back");
  // Phase 28 — shared website (Catalog mode) par "Book a free demo" (/demo) aur "Register your hospital"
  // (/signup) ke link. Single mode (Kamla / keycodehms) me API "Single" batati hai aur link dikhte hi nahi.
  const [signupOpen, setSignupOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE}/Catalog/info`)
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        if (!alive || !j || j.mode !== "Catalog") return;
        if (j.signupEnabled) setSignupOpen(true);
        if (j.demoRequestsEnabled) setDemoOpen(true);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Set Dynamic Greeting based on time
  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good Morning");
    else if (hour < 18) setGreeting("Good Afternoon");
    else setGreeting("Good Evening");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/Auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ loginId, password }),
      });

      // Server ka sandesh dikhate hain — shared website par "approval baaki hai", "hospital
      // band hai", "5 galat password, 15 minute ruko" jaise saaf jawab aate hain. Single mode
      // me 401 ka text pehle se "Invalid credentials" hi hai, isliye wahan kuch nahi badla.
      if (!response.ok) {
        const txt = await response.text().catch(() => "");
        throw new Error(readServerMessage(txt, "Invalid credentials"));
      }

      const data = await response.json();
      sessionStorage.setItem("token", data.token);
      sessionStorage.setItem("hospitalId", data.hospitalId);
      // Hospital ka naam har PRINT ke header me lagta hai — salary slip, voucher,
      // discharge summary, nurse handover.
      //
      // Pehle ye key SIRF tab set hoti thi jab koi Hospital Profile screen se profile
      // SAVE kare. Yaani har fresh login par hoti hi nahi thi, aur chaaron print ke
      // header me hospital ke naam ki jagah likha aata tha: "Hospital".
      // Discharge summary marij ke paas jaati hai, salary slip bank ke paas.
      if (data.hospitalName) sessionStorage.setItem("hospitalName", data.hospitalName);
      sessionStorage.setItem("loginId", loginId);
      sessionStorage.setItem("appThemeColor", data.themeColor || "#0088cc");
      sessionStorage.setItem("allowedModules", JSON.stringify(data.modules));
      // Pharmacy admin (PHARMACY me add+edit+delete teeno) → navbar me Master + Audit dikhega
      sessionStorage.setItem("isPharmacyAdmin", data.isPharmacyAdmin ? "1" : "0");
      // HRMS admin (HRMS me add+edit+delete teeno) → Payroll/Advances/masters menus dikhenge
      sessionStorage.setItem("isHrmsAdmin", data.isHrmsAdmin ? "1" : "0");
      sessionStorage.setItem("isInventoryAdmin", data.isInventoryAdmin ? "1" : "0");
      // Tier 1 — module guard iski jaanch karta hai (components/ModuleGuard.jsx).
      // Hospital admin ka adhikar is FLAG se aata hai, module list se nahi.
      sessionStorage.setItem("isHospitalAdmin", data.isHospitalAdmin ? "1" : "0");
      // Phase 28 — hospital ka plan (kaunse module mile). Single mode me koi rok nahi.
      saveTenancy(data);
      // Phase 29 — LabSureX (Pathology) judi hai? (src/labLink.js)
      saveLabLink(data);

      if (rememberMe) {
        localStorage.setItem("savedLoginId", loginId);
      }

      // Phase 29 — jiske paas SIRF Pathology hai uske liye HMS me kuch hai hi nahi:
      // seedha lab, isi tab me (/lab/open ticket le kar LabSureX khol deta hai).
      if (isPathologyOnly()) {
        window.location.replace("/lab/open");
        return;
      }

      // Shared website par jis hospital ke plan me HMS (core) hai hi nahi — sirf Pharmacy,
      // HRMS, Inventory ya Ward — use HMS dashboard par bhejna bekaar hai (har card 403).
      // Seedha uske product par. Single mode me productLanding() hamesha null.
      const landing = productLanding();
      if (landing) {
        window.location.replace(landing);
        return;
      }

      // Agar user ke paas SIRF Pharmacy module hai → seedha Pharmacy ERP layout kholein.
      // Warna (Pharmacy + koi aur module, ya HMS/ADMIN super-admin) → hospital dashboard.
      // Robust: khaali/space entries hata do, phir dekho pharmacy ke alawa koi module to nahi.
      const modules = Array.isArray(data.modules)
        ? data.modules.map((m) => String(m).trim().toUpperCase()).filter(Boolean)
        : [];
      const isSuperAdmin = modules.includes("HMS") || modules.includes("ADMIN");
      const hasPharmacy = modules.includes("PHARMACY");
      const otherModules = modules.filter((m) => m !== "PHARMACY");
      const isPharmacyOnly = hasPharmacy && !isSuperAdmin && otherModules.length === 0;
      console.log("[Login] modules:", modules, "| isPharmacyOnly:", isPharmacyOnly, "| other:", otherModules);

      if (isPharmacyOnly) {
        // 🔒 Hard redirect (full reload) — React ke login→layout re-render race ko poori tarah
        // bypass karne ke liye. sessionStorage me token/modules already set hain, reload pe
        // App unhe utha ke seedha Pharmacy layout dikhayega. (navigate() timing pe depend nahi.)
        window.location.replace("/pharmacy/sale");
        return;
      }

      onLoginSuccess({ isAuthenticated: true });
      navigate("/dashboard");
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  // --- Styles ---

  const containerStyle = {
    display: "flex",
    height: "100vh",
    width: "100%",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    overflow: "hidden"
  };

  // LEFT SIDE (As finalized)
  const leftSideStyle = {
    flex: "1.5",
    background: "linear-gradient(135deg, rgba(0, 50, 80, 0.92), rgba(0, 20, 40, 0.95)), url('https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?auto=format&fit=crop&w=1200&q=60')",
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "40px 60px",
    color: "white",
    position: "relative"
  };

  // RIGHT SIDE (Enhanced Background)
  const rightSideStyle = {
    flex: "1",
    backgroundColor: "#fcfcfc", // Thoda off-white aankhon ke liye soft
    backgroundImage: "radial-gradient(#e3e3e3 1px, transparent 1px)", // Subtle dot pattern
    backgroundSize: "20px 20px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px",
    position: "relative",
    boxShadow: "-10px 0 30px rgba(0,0,0,0.05)"
  };

  // Floating Blob Decoration (Right Side background)
  const blobStyle = {
    position: 'absolute',
    top: '-10%',
    right: '-10%',
    width: '300px',
    height: '300px',
    background: 'linear-gradient(135deg, #e0f7fa 0%, #e1f5fe 100%)',
    borderRadius: '50%',
    filter: 'blur(60px)',
    opacity: 0.6,
    zIndex: 0
  };

  const navbarStyle = {
    display: "flex", justifyContent: "space-between", alignItems: "center", padding: "15px 25px",
    background: "rgba(255, 255, 255, 0.1)", backdropFilter: "blur(12px)", borderRadius: "16px",
    border: "1px solid rgba(255, 255, 255, 0.15)", marginBottom: "40px"
  };

  const navLinkStyle = {
    color: "rgba(255, 255, 255, 0.8)", fontSize: "13px", fontWeight: "500", textDecoration: "none",
    marginRight: "20px", cursor: "pointer", transition: "color 0.2s"
  };

  const featureCardContainer = { display: "flex", gap: "15px", marginTop: "30px" };
  const featureCardStyle = {
    background: "rgba(0, 0, 0, 0.3)", padding: "15px", borderRadius: "12px",
    border: "1px solid rgba(255, 255, 255, 0.1)", flex: 1, backdropFilter: "blur(5px)"
  };

  // Form Styles
  const formWrapperStyle = {
    width: "100%", maxWidth: "380px", zIndex: 1, 
    background: "rgba(255,255,255,0.8)", // Glass effect on form
    padding: "30px", borderRadius: "20px",
    backdropFilter: "blur(10px)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.05)"
  };

  const inputWrapperStyle = (fieldName) => ({
    position: "relative", marginBottom: "20px", transition: "all 0.3s ease",
    transform: focusedInput === fieldName ? "translateY(-2px)" : "none"
  });

  const iconStyle = {
    position: "absolute", left: "15px", top: "50%", transform: "translateY(-50%)",
    color: "#007bff", fontSize: "18px", opacity: 0.7
  };

  const inputStyle = (fieldName) => ({
    width: "100%", height: "50px", paddingLeft: "45px", paddingRight: "15px",
    border: focusedInput === fieldName ? "2px solid #007bff" : "1px solid #e1e4e8",
    borderRadius: "10px", fontSize: "14px", 
    backgroundColor: focusedInput === fieldName ? "#fff" : "#f9f9f9",
    outline: "none", transition: "all 0.3s ease", color: "#333"
  });

  const labelStyle = {
    display: "block", marginBottom: "6px", fontSize: "12px", fontWeight: "700",
    color: "#555", textTransform: "uppercase", letterSpacing: "0.5px"
  };

  const buttonStyle = {
    width: "100%", height: "50px", background: "linear-gradient(90deg, #0062cc 0%, #00c6ff 100%)",
    border: "none", borderRadius: "10px", color: "white", fontSize: "15px", fontWeight: "700",
    letterSpacing: "0.5px", cursor: "pointer", boxShadow: "0 5px 15px rgba(0, 114, 255, 0.3)",
    transition: "transform 0.2s, box-shadow 0.2s", marginTop: "20px"
  };

  // Utility Links Style
  const utilityLinksStyle = {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    fontSize: "13px", marginTop: "10px", color: "#666"
  };

  const footerBadgeStyle = {
    position: "absolute", bottom: "30px", background: "#f1f3f5", padding: "8px 20px",
    borderRadius: "30px", fontSize: "12px", color: "#6c757d", display: "flex",
    alignItems: "center", gap: "8px", border: "1px solid #dee2e6", zIndex: 1
  };

  return (
    <div style={containerStyle}>
      
      {/* --- LEFT SIDE (Same as before) --- */}
      <div className="d-none d-md-flex" style={leftSideStyle}>
        <div>
            <div style={navbarStyle}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', background: '#00c6ff', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: 'bold' }}>H</div>
                    <span style={{ fontWeight: "700", letterSpacing: "0.5px", fontSize: "15px" }}>{IS_HMSERP ? "HMSERP" : "HMS PORTAL"}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={navLinkStyle}>Modules</span>
                    <span style={navLinkStyle}>Documentation</span>
                    <span style={navLinkStyle}>Support</span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.2)', padding: '5px 12px', borderRadius: '20px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span>📞</span> +91-1800-HMS-HELP
                </div>
            </div>
            <div style={{ marginTop: '40px' }}>
                <div style={{ color: '#00c6ff', fontWeight: 'bold', letterSpacing: '1px', fontSize: '13px', marginBottom: '10px' }}>ENTERPRISE EDITION V3.0</div>
                <h1 style={{ fontSize: "3.5rem", fontWeight: "800", lineHeight: "1.1", marginBottom: "15px" }}>Streamline Your <br/> Hospital Operations.</h1>
                <p style={{ fontSize: "1.1rem", opacity: 0.8, maxWidth: "90%", lineHeight: "1.6" }}>An integrated platform for OPD, IPD, Pharmacy, Lab, and Billing. Manage patient data securely with real-time analytics.</p>
                <div style={featureCardContainer}>
                    <div style={featureCardStyle}><div style={{ fontSize: '20px', marginBottom: '5px' }}>🛡️</div><h4 style={{ fontSize: '14px', margin: '0 0 5px 0' }}>Secure Data</h4><p style={{ fontSize: '11px', margin: 0, opacity: 0.7 }}>256-bit Encryption</p></div>
                    <div style={featureCardStyle}><div style={{ fontSize: '20px', marginBottom: '5px' }}>☁️</div><h4 style={{ fontSize: '14px', margin: '0 0 5px 0' }}>Cloud Sync</h4><p style={{ fontSize: '11px', margin: 0, opacity: 0.7 }}>Real-time Access</p></div>
                    <div style={featureCardStyle}><div style={{ fontSize: '20px', marginBottom: '5px' }}>📊</div><h4 style={{ fontSize: '14px', margin: '0 0 5px 0' }}>Analytics</h4><p style={{ fontSize: '11px', margin: 0, opacity: 0.7 }}>Smart Reports</p></div>
                </div>
            </div>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: "20px", display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "12px", opacity: 0.7 }}>
            <div>&copy; 2026 Keycodetech Pvt Ltd. All rights reserved.</div>
            <div style={{ display: 'flex', gap: '15px' }}>
              {/* Asli page — public/privacy-policy.html (Play Store isi pate ko maangta hai). Wo page
                  hmserp.in ka hai, isliye link sirf hmserp ke build me; Kamla par pehle jaisa likha hua. */}
              {IS_HMSERP
                ? <a href={`${process.env.PUBLIC_URL}/privacy-policy.html`} target="_blank" rel="noopener noreferrer"
                     style={{ color: "inherit", textDecoration: "none" }}>Privacy Policy</a>
                : <span>Privacy Policy</span>}
              <span>Terms of Service</span>
            </div>
        </div>
      </div>

      {/* --- RIGHT SIDE (ENHANCED) --- */}
      <div style={rightSideStyle}>
        
        {/* Subtle Background Blob */}
        <div style={blobStyle}></div>

        <div style={formWrapperStyle}>

          {/* HMSERP ka logo (21 Sep 2026). Form ke UPAR, kyunki baayan (neela) hissa mobile par
              chhup jaata hai — logo wahan hota to phone par dikhta hi nahi. Logo ka background
              transparent hai aur uska "HMS" gehra neela — isliye sirf is safed hisse par. */}
          {IS_HMSERP && (
            <div style={{ textAlign: "center", marginBottom: "18px" }}>
              <img
                src={`${process.env.PUBLIC_URL}/hmserp-logo.png`}
                alt="HMSERP — Hospital Management Software"
                style={{ width: "100%", maxWidth: "230px", height: "auto", display: "inline-block" }}
              />
            </div>
          )}

          <div className="mb-4">
            <span style={{ fontSize: "12px", color: "#00c6ff", fontWeight: "bold", textTransform: "uppercase" }}>{greeting}</span>
            <h2 style={{ fontSize: "26px", fontWeight: "800", color: "#2c3e50", marginTop: "5px" }}>Login to Account</h2>
            <p style={{ color: "#7f8c8d", fontSize: "14px" }}>Enter credentials to access admin panel.</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={inputWrapperStyle("loginId")}>
              <label htmlFor="loginId" style={labelStyle}>User ID / Email</label>
              <div style={{ position: "relative" }}>
                <span style={iconStyle}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                </span>
                <input type="text" id="loginId" style={inputStyle("loginId")} placeholder="Enter ID" value={loginId} onChange={(e) => setLoginId(e.target.value)} onFocus={() => setFocusedInput("loginId")} onBlur={() => setFocusedInput(null)} required />
              </div>
            </div>

            <div style={inputWrapperStyle("password")}>
              <label htmlFor="password" style={labelStyle}>Password</label>
              <div style={{ position: "relative" }}>
                <span style={iconStyle}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect><path d="M7 11V7a5 5 0 0 1 10 0v4"></path></svg>
                </span>
                <input type="password" id="password" style={inputStyle("password")} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} onFocus={() => setFocusedInput("password")} onBlur={() => setFocusedInput(null)} required />
              </div>
            </div>

            {/* Remember Me & Forgot Password */}
            <div style={utilityLinksStyle}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '5px' }}>
                    <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} style={{ accentColor: '#0088cc' }} />
                    Remember me
                </label>
                <a href="#" style={{ color: '#0088cc', textDecoration: 'none', fontWeight: '600' }} onClick={(e) => e.preventDefault()}>Forgot Password?</a>
            </div>

            {error && <div className="alert alert-danger mt-3" style={{ fontSize: "13px", padding: "10px", borderRadius: "8px" }}>{error}</div>}

            <button type="submit" style={buttonStyle} onMouseOver={(e) => e.target.style.transform = "translateY(-2px)"} onMouseOut={(e) => e.target.style.transform = "translateY(0)"} disabled={isLoading}>
              {isLoading ? "Verifying..." : "Secure Login"}
            </button>

            {/* Employee ka apna portal — hospital wale login se bilkul alag rasta.
                Sirf ek link hai; login yahan nahi hota, /ess/login par hota hai. */}
            <a
              href="/ess/login"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                marginTop: '14px', padding: '13px', borderRadius: '10px',
                border: '1px solid #ccfbf1', background: '#f0fdfa', color: '#0f766e',
                fontSize: '13.5px', fontWeight: 700, textDecoration: 'none',
              }}
            >
              <span style={{ fontSize: '16px' }}>👤</span>
              कर्मचारी हैं? अपना खाता यहाँ खोलिए · Employee login
            </a>

            {demoOpen && (
              <a
                href="/demo"
                onClick={(e) => { e.preventDefault(); navigate("/demo"); }}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  marginTop: '10px', padding: '13px', borderRadius: '10px',
                  border: '1px solid #dbeafe', background: '#eff6ff', color: '#1d4ed8',
                  fontSize: '13.5px', fontWeight: 700, textDecoration: 'none',
                }}
              >
                <span style={{ fontSize: '16px' }}>🏥</span>
                New hospital? Book a free demo
              </a>
            )}
            {signupOpen && (
              <a
                href="/signup"
                onClick={(e) => { e.preventDefault(); navigate("/signup"); }}
                style={{
                  display: 'block', textAlign: 'center', marginTop: '8px',
                  color: '#1d4ed8', fontSize: '12.5px', fontWeight: 600, textDecoration: 'none',
                }}
              >
                Ready to start? Register your hospital →
              </a>
            )}

            {/* Alternative Login Options */}
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
                <p style={{ fontSize: '12px', color: '#aaa' }}>Or login with</p>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', marginTop: '10px' }}>
                    <button type="button" style={{ border: '1px solid #eee', background: 'white', padding: '8px 15px', borderRadius: '50px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        📱 OTP
                    </button>
                    <button type="button" style={{ border: '1px solid #eee', background: 'white', padding: '8px 15px', borderRadius: '50px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                        🔳 QR Code
                    </button>
                </div>
            </div>

          </form>
        </div>

        <div style={footerBadgeStyle}>
          <span style={{ fontWeight: "400" }}>Technology Partner</span>
          <div style={{ width: "1px", height: "15px", background: "#ccc" }}></div>
          <span style={{ fontWeight: "700", color: "#2c3e50", letterSpacing: "0.5px" }}>KEYCODETECH PVT LTD</span>
        </div>
      </div>
    </div>
  );
};

export default Login;